import "../Management/Management.css";
import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useUser } from "../Components/Context/UserContext";
import { ContentManagement } from "./ContentManagement";
import { TrainingProcess } from "./TrainingProcess";
import { CustomerEngagement } from "./CustomerEngagement";
import { CompanyProfile } from "./CompanyProfile";
import { TrackingExpense } from "../Management/TrackingExpense";
import { Invoice } from "../Management/Invoice";
import {
  CompanyProfileProvider,
  useCompanyProfile,
} from "../Components/Context/CompanyProfileContext";
import logo from "../Components/Assets/Home-Header-Footer/hussme-white-web.png";
import invoice from "../Components/Assets/Management/invice-icn.png";
import engage_icn from "../Components/Assets/Management/sidebar-engage.png";
import Campaign_icn from "../Components/Assets/Management/email-marketing.png";
import { Campaign } from "./Campaign";
import { ToastContainer, toast, Zoom } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export function Management() {
  const { user, logout } = useUser();
  const [showLogout, setShowLogout] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [activeComponent, setActiveComponent] = useState(<ContentManagement />);
  const [activeIndex, setActiveIndex] = useState(0); // Track the active sidebar index
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  return (
    <CompanyProfileProvider>
      <ManagementContent
        user={user}
        logout={logout}
        showLogout={showLogout}
        setShowLogout={setShowLogout}
        showCompanyDropdown={showCompanyDropdown}
        setShowCompanyDropdown={setShowCompanyDropdown}
        activeComponent={activeComponent}
        setActiveComponent={setActiveComponent}
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        companyProfile={companyProfile}
        setCompanyProfile={setCompanyProfile}
        loading={loading}
        setLoading={setLoading}
        navigate={navigate}
      />
    </CompanyProfileProvider>
  );
}

function ManagementContent({
  user,
  logout,
  showLogout,
  setShowLogout,
  showCompanyDropdown,
  setShowCompanyDropdown,
  activeComponent,
  setActiveComponent,
  activeIndex,
  setActiveIndex,
  companyProfile,
  setCompanyProfile,
  loading,
  setLoading,
  navigate,
}) {
  const { hasGoogleSheetsAPI, companyProfile: contextCompanyProfile } =
    useCompanyProfile();
  const location = useLocation();

  // Function to toggle the logout dropdown
  const toggleLogout = (e) => {
    e.stopPropagation();
    setShowLogout(!showLogout);
    setShowCompanyDropdown(false);
  };

  // Function to toggle the company logo dropdown
  const toggleCompanyDropdown = (e) => {
    e.stopPropagation();
    setShowCompanyDropdown(!showCompanyDropdown);
    setShowLogout(false);
  };

  // Function to handle logout and navigate to home
  const handleLogoutAndNavigate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    logout();
    navigate("/");
  };

  // Function to handle logout
  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    setShowLogout(false);
    setShowCompanyDropdown(false);
  };

  // Function to navigate to change password page
  const handleChangePassword = (e) => {
    e.preventDefault();
    setShowLogout(false);
    setShowCompanyDropdown(false);
    navigate("/reset-password");
  };

  // Function to close dropdowns when clicking outside
  const closeDropdowns = () => {
    setShowLogout(false);
    setShowCompanyDropdown(false);
  };

  // Function to load components dynamically
  const loadComponent = (component, index) => {
    setActiveComponent(component);
    setActiveIndex(index);
  };

  // Fetch company profile data on component mount
  const fetchCompanyProfile = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/login");
        return;
      }

      const response = await axios.get(
        process.env.REACT_APP_API_URL + "/company/get-company-profile",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCompanyProfile(response.data);
    } catch (error) {
      console.error("Error fetching company profile:", error);

      // If profile not found, redirect to create profile
      if (error.response && error.response.status === 404) {
        navigate("/new-company");
      }
    } finally {
      setLoading(false);
    }
  };

  // **CHECK FREE TRIAL STATUS** on portal access
  const checkTrialStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      // Skip check if just created profile (coming from /new-company)
      // Give the server time to activate trial
      const fromNewCompany = location.state?.fromNewCompany;
      if (fromNewCompany) {
        // Just created profile, trial should be active
        toast.success("Welcome! Your 30-day free trial has started.", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
          autoClose: 3000,
        });
        return;
      }

      const response = await axios.get(
        process.env.REACT_APP_API_URL + "/company/check-trial-status",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const trialStatus = response.data;

      // If user doesn't have access (trial expired, no subscription)
      if (!trialStatus.hasAccess) {
        toast.error(trialStatus.message || "Please subscribe to continue using our services", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
          autoClose: 5000,
        });
        
        // Redirect to service page after 3 seconds
        setTimeout(() => {
          navigate("/service");
        }, 3000);
      } else if (trialStatus.isTrialActive && trialStatus.daysRemaining <= 7) {
        // Show warning if trial is ending soon
        toast.warning(`${trialStatus.daysRemaining} days remaining in your free trial`, {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
          autoClose: 4000,
        });
      }
    } catch (error) {
      console.error("Error checking trial status:", error);
    }
  };

  useEffect(() => {
    fetchCompanyProfile();
    checkTrialStatus(); // Check trial status on mount
  }, [navigate]);
  // Add click handler to close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      closeDropdowns();
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Helper function to get proper image URL
  const getImageUrl = (logoPath) => {
    if (!logoPath) return "https://i.ibb.co/4wrxz3pC/image-upload-icon.png";

    // If it's a full URL already (like a base64 string), return as is
    if (logoPath.startsWith("data:image")) return logoPath;

    // Otherwise, prepend the server URL
    // Remove /api from the URL when constructing image path since logo paths already include the correct structure
    const baseUrl = process.env.REACT_APP_API_URL.replace("/api", "");
    return baseUrl + logoPath;
  };

  if (loading) {
    return (
      <div className="loader-container">
        <span className="loader"></span>
      </div>
    );
  }

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        transition={Zoom}
      />
      <div className="mgmt-page" onClick={closeDropdowns}>
        {/* Start of navbar */}
        <div className="mgmt-navbar">
          {/* Left side - Company Logo */}
          <div
            className="company-logo-container"
            onClick={(e) => toggleCompanyDropdown(e)}
          >
            {companyProfile && companyProfile.logo ? (
              <img
                src={companyProfile?.logo}
                alt="https://i.ibb.co/Tx7tzcQ6/freepicdownloader-com-square-p-letter-logo-medium.jpg"
                className="company-logo"
                title="Click for options"
              />
            ) : (
              <div className="logo-placeholder">Logo</div>
            )}

            {showCompanyDropdown && (
              <div className="company-dropdown">
                <div className="dropdown-header">
                  {companyProfile?.companyName}
                </div>

                <Link
                  className="dropdown-item"
                  onClick={(e) =>
                    loadComponent(
                      <CompanyProfile onProfileUpdated={fetchCompanyProfile} />,
                      3
                    )
                  }
                >
                  Edit Profile
                </Link>
                <Link
                  className="dropdown-item logout-btn"
                  onClick={handleLogoutAndNavigate}
                >
                  Logout & Go Home
                </Link>
              </div>
            )}
          </div>

          {/* Center - Company Name */}
          <div className="center-company-name">
            {companyProfile.dba
              ? companyProfile.dba
              : companyProfile.companyName}
          </div>

          {/* Right side - Website Logo */}
          <div className="website-logo-container">
            <Link to="/">
              <img className="website-logo" src={logo} alt="Hussme" />
            </Link>
          </div>
        </div>
        {/* End of navbar */}

        {/* Start of Sidebar */}
        <div className="management-section">
          <div className="mgmt-sidebar">
            <ul>
              {/* Digital Content Management Component */}
              <li
                onClick={
                  activeIndex === 0
                    ? undefined
                    : () => loadComponent(<ContentManagement />, 0)
                }
                className={
                  activeIndex === 0 ? "active-sidebar-item no-cursor" : ""
                }
                style={
                  activeIndex === 0
                    ? { cursor: "default" }
                    : { cursor: "pointer" }
                }
              >
                <NavLink>
                  <svg
                    className="mgmt-sidebar-icn"
                    width="40px"
                    height="40px"
                    viewBox="0 0 16.00 16.00"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    stroke="none"
                  >
                    <defs>
                      <linearGradient
                        id="myGradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor="#ff7b2f" />
                        <stop offset="60%" stopColor="#8731f7" />
                      </linearGradient>
                    </defs>
                    <g id="SVGRepo_iconCarrier">
                      <path
                        d="m 4 0 c -1.644531 0 -3 1.355469 -3 3 v 10 c 0 1.644531 1.355469 3 3 3 h 3 c 0.550781 0 1 -0.449219 1 -1 s -0.449219 -1 -1 -1 h -3 c -0.570312 0 -1 -0.429688 -1 -1 v -10 c 0 -0.570312 0.429688 -1 1 -1 h 5.585938 l 3.414062 3.414062 v 6.585938 c 0 0.550781 0.449219 1 1 1 s 1 -0.449219 1 -1 v -7 c 0 -0.265625 -0.105469 -0.519531 -0.292969 -0.707031 l -4 -4 c -0.1875 -0.1875 -0.441406 -0.292969 -0.707031 -0.292969 z m 4.640625 6.167969 c -0.199219 0.003906 -0.402344 0.019531 -0.597656 0.046875 l 1.171875 2.074218 c 0.242187 0.425782 -0.027344 0.914063 -0.539063 1.210938 l -0.925781 0.53125 c -0.515625 0.289062 -1.089844 0.25 -1.332031 -0.175781 l -1.171875 -2.074219 c -0.25 0.308594 -0.476563 0.648438 -0.617188 1.019531 c -0.679687 1.761719 0.285156 3.46875 2.152344 3.804688 c 0.386719 0.074219 0.800781 0.054687 1.191406 0.007812 c 0.03125 -0.007812 1.859375 3.386719 1.859375 3.386719 l 4.167969 0.003906 l -3.199219 -4.957031 c 0.246094 -0.308594 0.445313 -0.652344 0.585938 -1.019531 c 0.679687 -1.765625 -0.285157 -3.472656 -2.148438 -3.808594 c -0.195312 -0.039062 -0.398437 -0.050781 -0.597656 -0.046875 z m 0 0"
                        fill="url(#myGradient)"
                        stroke="white"
                        strokeWidth="0"
                      />
                    </g>
                  </svg>

                  <div className="mgmt-sidebar-title">Digital Content</div>
                </NavLink>
              </li>
              {/* Customer Engagement Management Component - Only show if Google Sheets API is configured */}
              {hasGoogleSheetsAPI() && (
                <li
                  onClick={
                    activeIndex === 1
                      ? undefined
                      : () => loadComponent(<CustomerEngagement />, 1)
                  }
                  className={
                    activeIndex === 1 ? "active-sidebar-item no-cursor" : ""
                  }
                  style={
                    activeIndex === 1
                      ? { cursor: "default" }
                      : { cursor: "pointer" }
                  }
                >
                  <NavLink>
                    <img src={engage_icn} alt="" className="engage-icn" />
                    <span className="mgmt-sidebar-title">
                      Customer Engagement
                    </span>
                  </NavLink>
                </li>
              )}
              {/* Campaign Component */}
              <li
                onClick={
                  activeIndex === 2
                    ? undefined
                    : () => loadComponent(<Campaign />, 2)
                }
                className={
                  activeIndex === 2 ? "active-sidebar-item no-cursor" : ""
                }
                style={
                  activeIndex === 2
                    ? { cursor: "default" }
                    : { cursor: "pointer" }
                }
              >
                <NavLink>
                  <img src={Campaign_icn} alt="" className="engage-icn" />
                  <span className="mgmt-sidebar-title">Campaign</span>
                </NavLink>
              </li>

              {/* Document Training Process Component */}
              <li
                onClick={
                  activeIndex === 3
                    ? undefined
                    : () => loadComponent(<TrainingProcess />, 3)
                }
                className={
                  activeIndex === 3 ? "active-sidebar-item no-cursor" : ""
                }
                style={
                  activeIndex === 3
                    ? { cursor: "default" }
                    : { cursor: "pointer" }
                }
              >
                <NavLink>
                  <img
                    src="https://productionhussmestorage.blob.core.windows.net/hussmeassets/content-trai-icn.png"
                    className="mgmt-sidebar-icn"
                  />
                  <span className="mgmt-sidebar-title">Documents</span>
                </NavLink>
              </li>
              {/* Document Training Process Component */}
              {/* <li
                onClick={
                  activeIndex === 6
                    ? undefined
                    : () => loadComponent(<TrackingExpense/>, 6)
                }
                className={
                  activeIndex === 6 ? "active-sidebar-item no-cursor" : ""
                }
                style={
                  activeIndex === 6
                    ? { cursor: "default" }
                    : { cursor: "pointer" }
                }
              >
                <NavLink>
                  <img
                    src="https://productionhussmestorage.blob.core.windows.net/hussmeassets/expense-1.png"
                    className="mgmt-sidebar-icn"
                  />
                  <span className="mgmt-sidebar-title">Tracking Expense</span>
                </NavLink>
              </li> */}
              {/* Invoice Component */}
              {/* <li
                onClick={
                  activeIndex === 7
                    ? undefined
                    : () => loadComponent(<Invoice/>, 7)
                }
                className={
                  activeIndex === 7 ? "active-sidebar-item no-cursor" : ""
                }
                style={
                  activeIndex === 7
                    ? { cursor: "default" }
                    : { cursor: "pointer" }
                }
              >
                <NavLink>
                  <img
                    src="https://productionhussmestorage.blob.core.windows.net/hussmeassets/invice-icn.png"
                    className="mgmt-sidebar-icn"
                  />
                  <span className="mgmt-sidebar-title">Invoice</span>
                </NavLink>
              </li> */}

              {/* Company Profile Management Component */}
              <li
                onClick={
                  activeIndex === 4
                    ? undefined
                    : () =>
                        loadComponent(
                          <CompanyProfile
                            onProfileUpdated={fetchCompanyProfile}
                          />,
                          4
                        )
                }
                className={
                  activeIndex === 4 ? "active-sidebar-item no-cursor" : ""
                }
                style={
                  activeIndex === 4
                    ? { cursor: "default" }
                    : { cursor: "pointer" }
                }
              >
                <NavLink>
                  <svg
                    className="mgmt-sidebar-icn"
                    width="45px"
                    height="45px"
                    viewBox="0 0 32 32"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    stroke="none"
                  >
                    <defs>
                      <linearGradient
                        id="profileGradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor="#ff7b2f" />
                        <stop offset="60%" stopColor="#8731f7" />
                      </linearGradient>
                    </defs>
                    <g id="SVGRepo_iconCarrier">
                      <polygon
                        points="20,2.6 20,8 25.4,8"
                        fill="url(#profileGradient)"
                        stroke="white"
                      />
                      <path
                        d="M23.5,10H19c-0.6,0-1-0.4-1-1V2H7C6.4,2,6,2.4,6,3v26c0,0.6,0.4,1,1,1h18c0.6,0,1-0.4,1-1V12.5C26,11.1,24.9,10,23.5,10z M23,26H9c0-0.1,0-0.1,0-0.2c0.3-1.8,1.6-3.3,3.3-3.7c0.6-0.2,1-0.6,1.2-1.2C12.6,20,12,18.7,12,17.4v-2.1c0-1,0.4-2,1.2-2.6 c0.5-0.4,0.9-0.6,1.5-0.6c0.8-0.8,1.9-1.2,3-1.1c1.9,0.2,3.3,2,3.3,4.1v1.3c0,0.6-0.4,1-0.9,1c0,0-0.1,0-0.1,0.1 c0,1.3-0.6,2.5-1.5,3.3c0.2,0.6,0.6,1,1.1,1.2c1.7,0.5,3,1.9,3.3,3.8C23,25.9,23,25.9,23,26z"
                        fill="url(#profileGradient)"
                        stroke="white"
                        strokeWidth="0.3"
                      />
                    </g>
                  </svg>
                  <span className="mgmt-sidebar-title">Account</span>
                </NavLink>
              </li>
            </ul>
          </div>

          {/* Render Dynamic Component */}
          <div className="mgmt-content">{activeComponent}</div>
        </div>
      </div>
    </>
  );
}
